import styled from '@emotion/styled';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React from 'react';
import CourseAttributes from 'components/Course/CourseAttributes';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { buildEntityUrl, encodeSlug } from 'utils/seo';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import ArrowStandardIcon from 'assets/icons/arrow_standard.svg?react';
import CheckIcon from 'assets/icons/check.svg?react';
import ChevronIcon from 'assets/icons/chevron_right.svg?react';
import SchoolIcon from 'assets/icons/famicons_school.svg?react';
import ShieldIcon from 'assets/icons/iconamoon_shield-yes-fill.svg?react';
import MailIcon from 'assets/icons/mail.svg?react';
import PhoneIcon from 'assets/icons/phone.svg?react';
import StarIcon from 'assets/icons/star.svg?react';
import CheckedStarIcon from 'assets/icons/star_checked.svg?react';
import ShareIcon from 'assets/icons/share.svg?react';
import Footer from 'components/Footer';
import Header from 'components/Header';
import HeadlineBackground from 'components/HeadlineBackground';
import Helmet from 'components/Helmet';
import AddReviewAction from 'components/AddReviewAction';
import PageContent from 'components/PageContent';
import MainLayout from 'layouts/MainLayout';
import { getCourseById, addCourseToFavorites, removeCourseFromFavorites } from 'actions/educations';
import getCurrentId from 'helpers/getCurrentId';
import formatDirectionName from 'helpers/formatDirectionName';
import { isAuthenticated } from 'helpers/authToken';
import NeedAuthDialog from 'components/NeedAuthDialog';
import handleCopyUrlHelper from 'helpers/handleCopyUrl';
import Review from 'components/Review';
import LinearProgress from 'components/LinearProgress';
import { useCourseReviews } from 'hooks/useCourseReviews';
import SocialMedia from './SocialMedia';
import CoursesCard from './../Courses/components/CourseCard';
const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 48,
  fontWeight: 500,
  lineHeight: '56px',
  color: theme.palette.textPrimary.main,
  marginBottom: 10,
  [theme.breakpoints.down('sm')]: {
    fontSize: 32,
    lineHeight: '40px'
  }
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: 40,
  fontWeight: 600,
  lineHeight: '28px',
  color: theme.palette.textPrimary.main,
  marginBottom: 24,
  [theme.breakpoints.down('sm')]: {
    fontSize: 20,
    lineHeight: '24px'
  }
}));

const Headline = styled(Typography)(({ theme }) => ({
  fontSize: 32,
  fontWeight: 500,
  marginBottom: 32,
  [theme.breakpoints.down('sm')]: {
    fontSize: 24,
    lineHeight: '24px'
  }
}));

const Content = styled(PageContent)(({ pt, mb }) => ({
  paddingBottom: 0,
  paddingTop: pt || 40,
  marginBottom: mb || 0,
  position: 'relative',
  zIndex: 2
}));

const HeadlineBg = HeadlineBackground;

const Card = styled('div')(({ p, theme }) => ({
  borderRadius: 18,
  backgroundColor: 'rgba(250, 250, 250, 1)',
  border: '1px solid rgba(10, 10, 10, 1)',
  boxShadow: '4px 4px 0px 0px rgba(10, 10, 10, 1)',
  padding: p || 24,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: 16
  }
}));

const CardGrey = styled('div')(() => ({
  backgroundColor: 'rgba(244, 243, 246, 1)',
  borderRadius: 12,
  padding: 16,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1
}));

const List = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(3, 1fr)`,
  gap: 24,
  marginBottom: 8,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: `repeat(1, 1fr)`,
    gap: 16
  }
}));

const TagsWrapper = styled('div')(({ theme, hash }) => ({
  display: 'flex',
  gap: hash ? 8 : 18,
  marginTop: 8,
  marginBottom: 16,
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: hash ? 'column' : 'row'
  }
}));

const Tag = styled('span')(({ type }) => ({
  fontSize: 14,
  color: 'rgba(10, 10, 10, 1)',
  borderRadius: 38,
  padding: '4px 10px',
  fontFamily: 'Rubik-Regular',
  position: 'relative',
  paddingLeft: 20,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: 10,
    width: 6,
    height: 6,
    marginTop: -3,
    borderRadius: '50%'
  },
  ...((type === 1 || ['Фаховий молодший бакалавр', 'Бакалавр', 'Новачок'].includes(type)) && {
    backgroundColor: 'rgba(121, 201, 18, 0.2)',
    '&:before': {
      backgroundColor: 'rgba(121, 201, 18, 1)'
    }
  }),
  ...((type === 2 || ['Спеціаліст'].includes(type)) && {
    backgroundColor: 'rgba(255, 191, 0, 0.2)',
    '&:before': {
      backgroundColor: 'rgba(255, 191, 0, 1)'
    }
  }),
  ...((type === 3 || ['Магістр', 'Експерт'].includes(type)) && {
    backgroundColor: 'rgba(130, 130, 130, 0.2)',
    '&:before': {
      backgroundColor: 'rgba(130, 130, 130, 1)'
    }
  })
}));

const HashTag = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 400,
  color: theme.palette.primary.main,
  [theme.breakpoints.down('sm')]: {
    marginBottom: 0
  }
}));

const CourseDescription = styled(Typography)(({ mb, theme, mbsm }) => ({
  fontSize: 15,
  marginBottom: mb || 50,
  [theme.breakpoints.down('sm')]: {
    marginBottom: mbsm || 24
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  boxShadow: 'none',
  backgroundColor: 'rgba(10, 10, 10, 1)',
  fontSize: 18,
  fontWeight: 500,
  color: '#fff',
  '&:hover': {
    boxShadow: 'none',
    backgroundColor: 'rgba(10, 10, 10, 1)'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 20,
    width: '100%'
  }
}));

const CenterButtonWrapper = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 32,
  marginBottom: 60
}));

const LinkStyled = styled('div')(({ mb }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: '#fff',
  color: 'rgba(10, 10, 10, 1)',
  borderRadius: 8,
  padding: 16,
  marginBottom: mb || 16,
  cursor: 'pointer'
}));

const LinkTitleStyled = styled(Typography)(() => ({
  fontWeight: 500,
  fontSize: 16,
  lineHeight: '24px',
  color: 'rgba(10, 10, 10, 1)'
}));

const Flex = styled('div')(({ gap, theme }) => ({
  display: 'flex',
  gap: gap || 85,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: 16
  }
}));

const FlexItem = styled('div')(({ flex }) => ({
  flex: flex || 1
}));

const TextIcon = styled(Typography)(({ mb }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 15,
  lineHeight: '24px',
  marginBottom: mb || 0,
  color: 'rgba(10, 10, 10, 1)',
  '& svg': {
    marginRight: 4,
    flex: 1
  },
  '& span': { flex: 9 }
}));

const TextIonFlex = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 24,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: 16,
    alignItems: 'flex-start'
  }
}));

const EqualRow = styled('div')(({ theme, mb }) => ({
  display: 'flex',
  gap: 50,
  alignItems: 'stretch',
  marginBottom: mb || 0,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: 16,
    marginBottom: 32
  }
}));

const ArrowLeftIconStyled = styled(ArrowStandardIcon)(() => ({
  transform: 'rotate(180deg)'
}));

const BreadcrumbsWrapper = styled('div')(() => ({
  marginBottom: 24
}));

const ReviewItemWrapper = styled('div')(() => ({
  marginBottom: 40
}));

const SliderNav = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 24,
  marginTop: 16
}));

const NavButton = styled(Button)(() => ({
  minWidth: 48,
  height: 40,
  borderRadius: 8,
  boxShadow: 'none',
  background: 'transparent',
  color: 'rgba(10, 10, 10, 1)',
  '&:hover': {
    backgroundColor: 'rgba(238, 237, 242, 1)'
  }
}));

const SubtitleIconed = styled(Headline)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  position: 'relative',
  marginTop: 60,
  [theme.breakpoints.down('sm')]: {
    marginTop: 80
  }
}));

const CourseBenefit = styled(Typography)(({ noMargin }) => ({
  fontSize: 15,
  lineHeight: '24px',
  marginTop: noMargin ? 0 : 24,
  color: 'rgba(10, 10, 10, 1)'
}));

const CourseBenefitValue = styled(Typography)(() => ({
  fontWeight: 500,
  fontSize: 15,
  color: 'rgba(10, 10, 10, 1)',
  '& svg': { marginRight: 4 }
}));

const NavPlaceholder = styled('div')(({ theme }) => ({
  width: 200,
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const Row = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  flexDirection: 'column',
  marginTop: 20,
  gap: 5
}));

const Field = styled(Typography)(() => ({
  fontSize: 15,
  fontWeight: 500,
  color: 'rgba(10, 10, 10, 1)'
}));

const Value = styled(Typography)(() => ({
  fontSize: 15,
  fontWeight: 400,
  color: 'rgba(10, 10, 10, 1)'
}));

const FavoritesWrapper = styled('div')(({ pasm, theme }) => ({
  zIndex: 10,
  '& svg': {
    width: 27,
    height: 27
  },
  [theme.breakpoints.down('sm')]: {
    ...(pasm
      ? {
        position: 'absolute',
        right: 10,
        top: 10
      }
      : {})
  }
}));

const CoursePage = () => {
  const t = useTranslate('Course');
  const tEP = useTranslate('EducationProgram');
  const tP = useTranslate('Platform');
  const tHelmet = useTranslate('HELMET');
  const tDirections = useTranslate('Directions');
  const tPlatforms = useTranslate('Platforms');
  const tCourses = useTranslate('Courses');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [currentId, setCurrentId] = React.useState(getCurrentId(location));
  const [courseInfo, setCourseInfo] = React.useState(null);
  const [isFavorite, setIsFavorite] = React.useState(false);
  const [needAuth, setNeedAuth] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  React.useEffect(() => {
    const newId = getCurrentId(location);
    setCurrentId((prev) => {
      if (newId !== prev) {
        setCourseInfo(null); // Reset course info when navigating to a new course
        return newId;
      }
      return prev;
    });
  }, [location]);

  React.useEffect(() => {
    if (currentId) {
      setCourseInfo(null); // Show loading state
      dispatch(getCourseById(currentId)).then((data) => {
        if (data && typeof data === 'object') {
          if (Object.keys(data).length === 0) {
            navigate('/404', { replace: true });
            return;
          }
          setCourseInfo(data);
        } else {
          setCourseInfo(null);
          navigate('/404', { replace: true });
        }
      });
    }
  }, [currentId, dispatch, navigate]);

  React.useEffect(() => {
    setIsFavorite(Boolean(courseInfo?.isFavorite));
  }, [courseInfo?.isFavorite]);

  const handleFavoriteToggle = React.useCallback(
    (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();

      if (!isAuthenticated()) {
        setNeedAuth(true);
        return;
      }

      const next = !isFavorite;
      setIsFavorite(next);

      if (next) {
        dispatch(addCourseToFavorites(currentId));
      } else {
        dispatch(removeCourseFromFavorites(currentId));
      }
    },
    [dispatch, currentId, isFavorite]
  );

  const handleCloseNeedAuth = React.useCallback((e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setNeedAuth(false);
  }, []);

  const handleCopyUrl = React.useCallback((e) => handleCopyUrlHelper(e, { dispatch }), [dispatch]);

  const handleNavigateToPlatform = () => {
    navigate(`/education/platforms/${courseInfo?.platform?.name}/${courseInfo?.platform?._id}`);
  };

  const prevRef = React.useRef(null);
  const nextRef = React.useRef(null);

  const {
    reviews,
    loading: reviewsLoading,
    submitReview
  } = useCourseReviews(currentId, {
    limit: 50
  });

  const canonicalUrl = React.useMemo(() => {
    if (!courseInfo) return undefined;
    const slug = courseInfo.slug || encodeSlug(courseInfo.courseName);
    return buildEntityUrl('course', slug, courseInfo._id);
  }, [courseInfo]);

  const breadcrumbs = React.useMemo(() => {
    const items = [
      { link: '/', label: 'Home' },
      { link: '/education', label: 'Education' },
      { link: '/education/platforms', label: tPlatforms('Title'), raw: true },
      { link: '/education/platforms/courses', label: tCourses('Title'), raw: true }
    ];
    
    if (courseInfo?.courseName) {
      items.push({
        link: location.pathname,
        label: courseInfo.courseName,
        raw: true
      });
    }
    
    return items;
  }, [courseInfo, location.pathname, tPlatforms, tCourses]);

  return (
    <>
      <Helmet
        meta={{
          title:
            courseInfo?.courseName && courseInfo?.platform?.name
              ? `${t('CourseTitle')} ${courseInfo.courseName} - ${courseInfo.platform.name}`
              : t('CourseTitle'),
          description: courseInfo?.courseDescriptions?.shortDescription || tHelmet('courseDescription'),
          ogTitle:
            courseInfo?.courseName && courseInfo?.platform?.name
              ? `${t('CourseTitle')} ${courseInfo.courseName} - ${courseInfo.platform.name}`
              : tHelmet('courseOgTitle'),
          ogDescription: courseInfo?.courseDescriptions?.shortDescription || tHelmet('courseOgDescription')
        }}
        canonicalUrl={canonicalUrl}
      />
      <MainLayout>
        <Header />

        <HeadlineBg />

        <Content>
          <React.Suspense fallback={null}>
            <BreadcrumbsWrapper>
              <Breadcrumbs breadcrumbs={breadcrumbs} />
            </BreadcrumbsWrapper>
          </React.Suspense>

          <Card>
            <Flex>
              <FlexItem flex={2}>
                <TagsWrapper>
                  {(courseInfo?.proficiency || []).map((l, idx) => (
                    <Tag key={`${l}-${idx}`} type={l}>
                      {l}
                    </Tag>
                  ))}
                </TagsWrapper>

                <Title variant="h1">{courseInfo?.courseName || ''}</Title>

                <TagsWrapper hash={true}>
                  {(courseInfo?.directions || []).map((tag, idx) => (
                    <HashTag key={idx}>#{formatDirectionName(tag, tDirections)}</HashTag>
                  ))}
                </TagsWrapper>

                <CourseDescription>
                  {courseInfo?.courseDescriptions?.shortDescription || ''}
                </CourseDescription>

                <Flex gap={16}>
                  <ActionButton
                    component={courseInfo?.courseUrl ? 'a' : 'button'}
                    href={courseInfo?.courseUrl || undefined}
                    target={courseInfo?.courseUrl ? '_blank' : undefined}
                    rel={courseInfo?.courseUrl ? 'noopener noreferrer' : undefined}
                    variant="contained"
                    color="primary"
                    aria-label={t('handleRegister')}>
                    {t('handleRegister')}
                  </ActionButton>

                  <FavoritesWrapper pasm={true}>
                    <IconButton aria-label="favorite" onClick={handleFavoriteToggle}>
                      {isFavorite ? <CheckedStarIcon /> : <StarIcon />}
                    </IconButton>
                  </FavoritesWrapper>

                  <FavoritesWrapper>
                    <IconButton aria-label="share" onClick={handleCopyUrl}>
                      <ShareIcon />
                    </IconButton>
                  </FavoritesWrapper>
                </Flex>
              </FlexItem>

              <FlexItem>
                <LinkStyled onClick={handleNavigateToPlatform}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {courseInfo?.platform?.logo ? (
                      <img
                        src={`${courseInfo.platform.logo}/public`}
                        alt={courseInfo?.platform?.name || 'logo'}
                        style={{ height: '32px', maxWidth: '65px', objectFit: 'contain' }}
                      />
                    ) : null}
                    <LinkTitleStyled>{courseInfo?.platform?.name || ''}</LinkTitleStyled>
                  </div>
                  <ChevronIcon />
                </LinkStyled>

                <CardGrey>
                  <CourseAttributes
                    translateNs="Course"
                    duration={courseInfo?.courseDurationDays}
                    languages={courseInfo?.courseLanguage || []}
                    studyFormat={courseInfo?.studyFormat || []}
                    minCostValue={courseInfo?.minCostValue}
                    maxCostValue={courseInfo?.maxCostValue}
                    costCurrency={t(courseInfo?.costCurrency)}
                    isFree={courseInfo?.isFree}
                    startDate={courseInfo?.startDate}
                  />
                </CardGrey>
              </FlexItem>
            </Flex>
          </Card>
        </Content>
        <NeedAuthDialog handleClose={handleCloseNeedAuth} needAuth={needAuth} />

        <Content pt={'50px'}>
          <EqualRow mb={45}>
            <FlexItem>
              <CardGrey>
                {courseInfo?.learningStructure?.materialsAccess?.hasAccess ? (
                  <>
                    <CourseBenefit noMargin={true}>{t('MaterialsAccess')}</CourseBenefit>
                    <CourseBenefitValue>
                      <CheckIcon />
                      {courseInfo?.learningStructure?.materialsAccess?.description || t('Forever')}
                    </CourseBenefitValue>
                  </>
                ) : null}

                {courseInfo?.learningStructure?.homeworkAfterLesson ||
                  courseInfo?.learningStructure?.projectDefense ||
                  courseInfo?.learningStructure?.theoryPractice ? (
                  <>
                    <CourseBenefit>{t('WorkTypes')}</CourseBenefit>
                    {courseInfo?.learningStructure?.homeworkAfterLesson ? (
                      <CourseBenefitValue>
                        <CheckIcon />
                        {t('Homework')}
                      </CourseBenefitValue>
                    ) : null}
                    {courseInfo?.learningStructure?.theoryPractice ? (
                      <CourseBenefitValue>
                        <CheckIcon />
                        {t('TheoryPractice')}
                      </CourseBenefitValue>
                    ) : null}
                    {courseInfo?.learningStructure?.projectDefense ? (
                      <CourseBenefitValue>
                        <CheckIcon />
                        {t('FinalProject')}
                      </CourseBenefitValue>
                    ) : null}
                  </>
                ) : null}

                {courseInfo?.certificateProvide ? (
                  <>
                    <CourseBenefit>{t('Certificate')}</CourseBenefit>
                    <CourseBenefitValue>
                      <CheckIcon />
                      {t('SchoolCertificate')}
                    </CourseBenefitValue>
                  </>
                ) : null}

                {courseInfo?.courseSkills?.length ? (
                  <>
                    <CourseBenefit>{t('SkillsYouGet')}</CourseBenefit>
                    {(courseInfo?.courseSkills || []).map((s, i) => (
                      <CourseBenefitValue key={i}>
                        <CheckIcon />
                        {s}
                      </CourseBenefitValue>
                    ))}
                  </>
                ) : null}

                {(courseInfo?.bonuses || []).length || courseInfo?.learningStructure?.employment ? (
                  <>
                    <CourseBenefit>{t('Bonuses')}</CourseBenefit>
                  </>
                ) : null}

                {(courseInfo?.bonuses || []).length ? (
                  <>
                    {(courseInfo?.bonuses || []).map((b, i) => (
                      <CourseBenefitValue key={i}>
                        <CheckIcon />
                        {b}
                      </CourseBenefitValue>
                    ))}
                  </>
                ) : null}

                {courseInfo?.learningStructure?.employment ? (
                  <>
                    <CourseBenefitValue>
                      <CheckIcon />
                      {t('EmploymentHelp')}
                    </CourseBenefitValue>
                  </>
                ) : null}
              </CardGrey>
            </FlexItem>
            <FlexItem flex={2}>
              <CourseDescription mb={40}>
                {(courseInfo?.courseDescriptions?.targetDescription || '')
                  .split('\n')
                  .map((item, index) => {
                    return (
                      <span key={index} style={{ display: 'block', marginBottom: !item ? 10 : 0 }}>
                        {item}
                      </span>
                    );
                  })}

                {courseInfo?.learningStructure?.mentors ? (
                  <Row>
                    <Field component={'span'}>{t('Team')}:</Field>
                    <Value component={'span'}>{courseInfo?.learningStructure?.mentors}</Value>
                  </Row>
                ) : null}
              </CourseDescription>

              <ActionButton
                component={courseInfo?.courseUrl ? 'a' : 'button'}
                href={courseInfo?.courseUrl || undefined}
                target={courseInfo?.courseUrl ? '_blank' : undefined}
                rel={courseInfo?.courseUrl ? 'noopener noreferrer' : undefined}
                variant="contained"
                color="primary"
                aria-label={t('handleRegister')}>
                {t('handleRegister')}
              </ActionButton>
            </FlexItem>
          </EqualRow>

          <Headline>{t('AboutSchool')}</Headline>

          <Card p={32} noMargin={true}>
            <Flex>
              <FlexItem flex={3}>
                <TextIonFlex>
                  <TextIcon>
                    <SchoolIcon />
                    {tP('OnlinePlatform')}
                  </TextIcon>
                  <TextIcon>
                    <ShieldIcon />
                    {tP('VerifiedInstitution')}
                  </TextIcon>
                </TextIonFlex>
                <CardTitle>{courseInfo?.platform?.name || ''}</CardTitle>
                {courseInfo?.platform?.description ? (
                  <CourseDescription mb={24} mbsm={'0px'}>
                    {courseInfo.platform.description}
                  </CourseDescription>
                ) : null}
              </FlexItem>
              <FlexItem>
                <CardGrey>
                  <LinkStyled mb={16} onClick={handleNavigateToPlatform}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {courseInfo?.platform?.logo ? (
                        <img
                          src={`${courseInfo.platform.logo}/public`}
                          alt={courseInfo?.platform?.name || 'logo'}
                          style={{ height: '32px', maxWidth: '65px', objectFit: 'contain' }}
                        />
                      ) : null}
                      <LinkTitleStyled>{courseInfo?.platform?.name || ''}</LinkTitleStyled>
                    </div>
                    <ChevronIcon />
                  </LinkStyled>

                  <div style={{ flex: 1 }} />
                  <span>
                    <SocialMedia list={courseInfo?.platform?.socialMedia} />
                  </span>

                  {(() => {
                    const phones = courseInfo?.platform?.contactInfo?.[0]?.phone || [];
                    const cleanTel = (p) => (p || '').replace(/[^\d+]/g, '');
                    return phones.length ? (
                      <TextIcon mb={16}>
                        <PhoneIcon />
                        <span>
                          {phones.map((p, i) => (
                            <React.Fragment key={`${p}-${i}`}>
                              {i > 0 ? <div></div> : null}
                              <a
                                href={`tel:${cleanTel(p)}`}
                                style={{ color: 'inherit', textDecoration: 'none' }}>
                                {p}
                              </a>
                            </React.Fragment>
                          ))}
                        </span>
                      </TextIcon>
                    ) : null;
                  })()}
                  {(() => {
                    const emails = courseInfo?.platform?.contactInfo?.[0]?.email || [];
                    return emails.length ? (
                      <TextIcon mb={16}>
                        <MailIcon />
                        <span>
                          {emails.map((e, i) => (
                            <React.Fragment key={`${e}-${i}`}>
                              {i > 0 ? <span style={{ margin: '0 6px' }}>|</span> : null}
                              <a
                                href={`mailto:${e}`}
                                style={{ color: 'inherit', textDecoration: 'none' }}>
                                {e}
                              </a>
                            </React.Fragment>
                          ))}
                        </span>
                      </TextIcon>
                    ) : null;
                  })()}
                </CardGrey>
              </FlexItem>
            </Flex>
          </Card>

          <SubtitleIconed>{t('ReviewsTitle')}</SubtitleIconed>

          <ReviewItemWrapper>
            {reviews.length > 0 ? (
              <Swiper
                modules={[Navigation]}
                navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
                onBeforeInit={(swiper) => {
                  swiper.params.navigation.prevEl = prevRef.current;
                  swiper.params.navigation.nextEl = nextRef.current;
                }}
                loop
                loopAdditionalSlides={2}
                spaceBetween={16}
                slidesPerView={2}
                breakpoints={{
                  0: { slidesPerView: 1, spaceBetween: 12 },
                  768: { slidesPerView: 2, spaceBetween: 16 }
                }}>
                {reviews.map((r, i) => (
                  <SwiperSlide key={r._id || i}>
                    <Review
                      idx={i}
                      name={r.userName}
                      text={r.text}
                      rating={r.mark}
                      markArray={r.markArray}
                      userAvatar={r.userAvatar}
                      createdAt={r.createdAt}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <Typography variant="body2" sx={{ display: 'block', mt: 2 }}>
                {t('ReviewsEmpty')}
              </Typography>
            )}

            <SliderNav>
              <NavPlaceholder />

              <div>
                <NavButton ref={prevRef} aria-label="Previous reviews">
                  <ArrowLeftIconStyled />
                </NavButton>
                <NavButton ref={nextRef} aria-label="Next reviews">
                  <ArrowStandardIcon />
                </NavButton>
              </div>

              <AddReviewAction
                buttonProps={{ variant: 'standard' }}
                mode="complex"
                onSubmit={async ({ ratings, message }) => {
                  await submitReview({ ratings, message });
                }}>
                {tEP('AddReview')}
              </AddReviewAction>
            </SliderNav>
          </ReviewItemWrapper>
          {reviewsLoading && <LinearProgress />}

          <CenterButtonWrapper>
            <ActionButton
              fz={18}
              component={courseInfo?.courseUrl ? 'a' : 'button'}
              href={courseInfo?.courseUrl || undefined}
              target={courseInfo?.courseUrl ? '_blank' : undefined}
              rel={courseInfo?.courseUrl ? 'noopener noreferrer' : undefined}
              variant="contained"
              color="primary"
              aria-label={t('handleSearch')}>
              {t('handleRegister')}
            </ActionButton>
          </CenterButtonWrapper>

          {courseInfo?.similarCourses?.length > 0 && (
            <>
              <SubtitleIconed>{t('SimilarCourses')}</SubtitleIconed>

              <List>
                {(courseInfo?.similarCourses || []).map((item, idx) => (
                  <CoursesCard key={idx} item={item} />
                ))}
              </List>
            </>
          )}
        </Content>

        <Footer />
      </MainLayout>
    </>
  );
};

export default CoursePage;
